import { useEffect, useState } from 'react'
import { api } from '../../lib'
import { Loading } from '../../app/ui'
import { Err, PartnerPage, SetupFooter, useAction, useSetup } from './common'

const TAGS = ['inference', 'sandboxes', 'gpu', 'post_training', 'other']
// Same words as the directory's layer filter.
const TAG_LABEL: Record<string, string> = {
  inference: 'Inference', sandboxes: 'Sandboxes / VMs', gpu: 'GPU', post_training: 'Post-training', other: 'Other',
}

export function VerifyPage() {
  return <PartnerPage>{(pid) => <VerifyBody pid={pid} />}</PartnerPage>
}

function VerifyBody({ pid }: { pid: string }) {
  const s = useSetup(pid)
  const empty = {
    description: '',
    stack_tags: [] as string[],
    legal_name: '',
    line1: '',
    city: '',
    state: '',
    postal_code: '',
    billing_email: '',
  }
  const [f, setF] = useState(empty)
  const [saved, setSaved] = useState(empty)
  const [noTag, setNoTag] = useState(false)
  useEffect(() => {
    const p = s.data?.partner
    if (!p) return
    const v = {
      description: p.description ?? '',
      stack_tags: p.stack_tags ?? [],
      legal_name: p.legal_name ?? '',
      line1: p.address?.line1 ?? '',
      city: p.address?.city ?? '',
      state: p.address?.state ?? '',
      postal_code: p.address?.postal_code ?? '',
      billing_email: p.billing_email ?? '',
    }
    setF(v)
    setSaved(v)
  }, [s.data?.partner])
  const a = useAction(() =>
    api('PATCH', `/api/partners/${pid}`, {
      description: f.stack_tags.includes('other') ? f.description : undefined,
      stack_tags: f.stack_tags,
      legal_name: f.legal_name,
      address: { line1: f.line1, city: f.city, state: f.state, postal_code: f.postal_code },
      billing_email: f.billing_email,
    }),
  )
  if (!s.data) return s.error ? <Err error={s.error} /> : <Loading />
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value })
  return (
    <form
      className="page narrow stack"
      onSubmit={(e) => {
        e.preventDefault()
        if (!f.stack_tags.length) return setNoTag(true)
        a.run(undefined)
      }}
    >
      <h1>Company details</h1>
      <div className="chips">
        {TAGS.map((t) => (
          <button
            type="button"
            key={t}
            aria-pressed={f.stack_tags.includes(t)}
            onClick={() => (setNoTag(false), setF({ ...f, stack_tags: f.stack_tags.includes(t) ? f.stack_tags.filter((x) => x !== t) : [...f.stack_tags, t] }))}
          >
            {TAG_LABEL[t]}
          </button>
        ))}
      </div>
      {noTag && <div className="error-box" role="alert" data-testid="partners-pick-tag">Pick what you sell.</div>}
      {f.stack_tags.includes('other') && (
        <label>
          What do you sell?
          <input data-testid="partners-description" value={f.description} onChange={set('description')} required />
        </label>
      )}
      <label>
        Legal name
        <input data-testid="partners-legal-name" value={f.legal_name} onChange={set('legal_name')} required />
      </label>
      <label>
        Street address
        <input data-testid="partners-address-line1" value={f.line1} onChange={set('line1')} required />
      </label>
      <div className="row">
        <input data-testid="partners-address-city" aria-label="City" placeholder="City" style={{ flexGrow: 2 }} value={f.city} onChange={set('city')} required />
        <input data-testid="partners-address-state" aria-label="State" placeholder="State" value={f.state} onChange={set('state')} required />
        <input data-testid="partners-address-zip" aria-label="ZIP" placeholder="ZIP" value={f.postal_code} onChange={set('postal_code')} required />
      </div>
      <label>
        Billing email
        <input data-testid="partners-billing-email" type="email" value={f.billing_email} onChange={set('billing_email')} required />
      </label>
      <Err error={a.error} />
      <button className="primary" data-testid="partners-verify-save" disabled={a.busy}>
        Save
      </button>
      {a.done && <p data-testid="partners-verified">Saved.</p>}
      <SetupFooter pid={pid} step="verify" disabled={a.busy || !saved.stack_tags.length || JSON.stringify(f) !== JSON.stringify(saved)} />
    </form>
  )
}
